'use client';

import React, { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchAlertSettings, saveAlertSettings } from '../../redux/actions/alertSettingsActions';

const VARIABLES = [
  { key: '{NomEntreprise}',  label: 'Nom entreprise',   icon: 'business' },
  { key: '{NomUtilisateur}', label: 'Nom utilisateur',  icon: 'person' },
  { key: '{DateSoumission}', label: 'Date soumission',  icon: 'event' },
  { key: '{NomFichier}',     label: 'Nom fichier',      icon: 'insert_drive_file' },
  { key: '{IDCommande}',     label: 'ID commande',      icon: 'tag' },
  { key: '{Separateur}',     label: 'Séparateur',       icon: 'drag_handle' },
];

const PREVIEW_VALUES = {
  '{NomEntreprise}':  'acme-corp',
  '{NomUtilisateur}': 'jean-dupont',
  '{DateSoumission}': '2026-05-11',
  '{NomFichier}':     'piece-v2.stl',
  '{IDCommande}':     '42',
};

function resolvePreview(template, prefix, suffix, separator) {
  let name = template;
  for (const { key } of VARIABLES) {
    const value = key === '{Separateur}' ? separator : (PREVIEW_VALUES[key] || '');
    name = name.split(key).join(value);
  }
  const esc = separator.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  name = name
    .replace(new RegExp(`(${esc}){2,}`, 'g'), separator)
    .replace(new RegExp(`^${esc}+|${esc}+$`, 'g'), '');
  if (prefix) name = `${prefix}${separator}${name}`;
  if (suffix) {
    const dotIdx = name.lastIndexOf('.');
    if (dotIdx > 0) {
      name = `${name.slice(0, dotIdx)}${separator}${suffix}${name.slice(dotIdx)}`;
    } else {
      name = `${name}${separator}${suffix}`;
    }
  }
  return name || 'apercu-fichier.stl';
}

const FileNamingSettings = () => {
  const dispatch = useDispatch();
  const { settings, loading, saving } = useSelector(state => state.alertSettings);

  const [template, setTemplate]   = useState('{NomEntreprise}{Separateur}{NomUtilisateur}{Separateur}{DateSoumission}{Separateur}{NomFichier}');
  const [prefix, setPrefix]       = useState('');
  const [suffix, setSuffix]       = useState('');
  const [separator, setSeparator] = useState('-');
  const [saveSuccess, setSaveSuccess] = useState(false);

  const templateRef = useRef(null);

  useEffect(() => { dispatch(fetchAlertSettings()); }, [dispatch]);

  useEffect(() => {
    if (!settings) return;
    const s = settings.find(s => s.alert_key === 'file_naming');
    if (s?.config) {
      if (s.config.template  !== undefined) setTemplate(s.config.template);
      if (s.config.prefix    !== undefined) setPrefix(s.config.prefix);
      if (s.config.suffix    !== undefined) setSuffix(s.config.suffix);
      if (s.config.separator !== undefined) setSeparator(s.config.separator);
    }
  }, [settings]);

  const insertVariable = (varKey) => {
    const el = templateRef.current;
    if (!el) return;
    const start = el.selectionStart;
    const end   = el.selectionEnd;
    const next  = template.slice(0, start) + varKey + template.slice(end);
    setTemplate(next);
    setTimeout(() => {
      el.focus();
      el.setSelectionRange(start + varKey.length, start + varKey.length);
    }, 0);
  };

  const handleSave = async () => {
    if (!settings) return;
    const updated = settings.map(s =>
      s.alert_key !== 'file_naming' ? s : { ...s, config: { template, prefix, suffix, separator } }
    );
    await dispatch(saveAlertSettings(updated));
    setSaveSuccess(true);
    setTimeout(() => setSaveSuccess(false), 3000);
  };

  const preview = resolvePreview(template, prefix, suffix, separator);

  return (
    <div className="fn-page">
      <div className="fn-page-header">
        <div className="fn-page-header-top">
          <h1 className="fn-page-title">
            <i className="material-icons">drive_file_rename_outline</i>
            Format des noms de fichiers
          </h1>
          <div className="fn-header-actions">
            {saveSuccess && (
              <span className="fn-save-feedback">
                <i className="material-icons">check_circle</i>
                Enregistré avec succès
              </span>
            )}
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleSave}
              disabled={saving || loading}
            >
              {saving ? 'Enregistrement...' : 'Enregistrer'}
            </button>
          </div>
        </div>
        <p className="fn-page-subtitle">
          Définissez comment les fichiers sont nommés lors du téléchargement par les administrateurs.
        </p>
      </div>

      {loading ? (
        <div className="loading-spinner" />
      ) : (
        <>
          {/* ── Séparateur ── */}
          <div className="fn-card">
            <div className="fn-card-header">
              <i className="material-icons">drag_handle</i>
              <h2 className="fn-card-title">Séparateur</h2>
            </div>
            <div className="fn-card-body">
              <p className="fn-card-desc">
                Caractère inséré entre les parties du nom lorsque vous utilisez la variable <code>{'{Separateur}'}</code>.
              </p>
              <div className="fn-separator-row">
                <input
                  type="text"
                  className="form-control fn-separator-input"
                  value={separator}
                  onChange={e => setSeparator(e.target.value)}
                  placeholder="-"
                  maxLength={5}
                />
                <span className="fn-separator-hint">
                  Exemples courants&nbsp;: <code>-</code> &nbsp;|&nbsp; <code>_</code> &nbsp;|&nbsp; <code>.</code>
                </span>
              </div>
            </div>
          </div>

          {/* ── Template ── */}
          <div className="fn-card">
            <div className="fn-card-header">
              <i className="material-icons">code</i>
              <h2 className="fn-card-title">Template du nom</h2>
            </div>
            <div className="fn-card-body">
              <p className="fn-card-desc">
                Cliquez sur une variable pour l'insérer à la position du curseur dans le template.
              </p>
              <div className="fn-variables">
                {VARIABLES.map(({ key, label, icon }) => (
                  <button
                    key={key}
                    type="button"
                    className="fn-var-chip"
                    onClick={() => insertVariable(key)}
                    title={`Insérer ${key}`}
                  >
                    <i className="material-icons">{icon}</i>
                    <span className="fn-var-chip-label">{label}</span>
                  </button>
                ))}
              </div>
              <div className="fn-template-wrap">
                <textarea
                  ref={templateRef}
                  className="form-control fn-template"
                  value={template}
                  onChange={e => setTemplate(e.target.value)}
                  rows={3}
                  spellCheck={false}
                  placeholder="Ex : {NomEntreprise}{Separateur}{NomUtilisateur}"
                />
              </div>
            </div>
          </div>

          {/* ── Préfixe / Suffixe ── */}
          <div className="fn-card">
            <div className="fn-card-header">
              <i className="material-icons">text_fields</i>
              <h2 className="fn-card-title">Préfixe &amp; Suffixe</h2>
            </div>
            <div className="fn-card-body">
              <p className="fn-card-desc">
                Texte fixe ajouté avant ou après le nom généré, séparé par le séparateur.
              </p>
              <div className="fn-affix-row">
                <div className="form-group" style={{ margin: 0 }}>
                  <div className="fn-affix-label">
                    <i className="material-icons">first_page</i>
                    Préfixe
                  </div>
                  <input
                    type="text"
                    className="form-control"
                    value={prefix}
                    onChange={e => setPrefix(e.target.value)}
                    placeholder="Ex : alphafusion"
                  />
                </div>
                <div className="form-group" style={{ margin: 0 }}>
                  <div className="fn-affix-label">
                    <i className="material-icons">last_page</i>
                    Suffixe
                  </div>
                  <input
                    type="text"
                    className="form-control"
                    value={suffix}
                    onChange={e => setSuffix(e.target.value)}
                    placeholder="Ex : final"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* ── Aperçu ── */}
          <div className="fn-card">
            <div className="fn-card-header">
              <i className="material-icons">visibility</i>
              <h2 className="fn-card-title">Aperçu</h2>
            </div>
            <div className="fn-card-body">
              <p className="fn-card-desc">Rendu avec des valeurs fictives — mis à jour en temps réel.</p>
              <div className="fn-preview">
                <div className="fn-preview-icon">
                  <i className="material-icons">insert_drive_file</i>
                </div>
                <div className="fn-preview-content">
                  <div className="fn-preview-filename">{preview}</div>
                  <div className="fn-preview-meta">Nom du fichier au téléchargement</div>
                </div>
              </div>
            </div>
          </div>

        </>
      )}
    </div>
  );
};

export default FileNamingSettings;
